// Formularios creados: lista con filtros (tipo, estado, carpeta), búsqueda, duplicar y eliminar.
import type { JSX } from "react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, ExternalLink, Plus, Trash2 } from "lucide-react";
import { api, ApiError } from "../../../lib/api.ts";
import { useMe } from "../../../lib/auth.ts";
import { formatDate } from "../../../lib/format.ts";
import { Badge, Button, Card, EmptyState, ErrorState, Field, Input, Modal, Select, TableSkeleton } from "../../../components/ui.tsx";
import { SearchInput } from "../../../components/SearchInput.tsx";
import { toast } from "../../../components/toast.tsx";
import { FolderNav, KIND_LABEL, type FormKind, type FormListRow, type MktForm, useMktOptions } from "./shared.tsx";

export function FormList(): JSX.Element {
  const admin = Boolean(useMe().data?.admin);
  const nav = useNavigate();
  const qc = useQueryClient();
  const opts = useMktOptions();
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [status, setStatus] = useState("");
  const [folder, setFolder] = useState("");
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [newKind, setNewKind] = useState<FormKind>("embedded");

  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (kind) params.set("kind", kind);
  if (status) params.set("status", status);
  if (folder) params.set("folder", folder);
  const list = useQuery<{ rows: FormListRow[] }>({
    queryKey: ["mkt-forms", q, kind, status, folder],
    queryFn: () => api.get(`/mkt/forms${params.toString() ? `?${params}` : ""}`),
  });
  const inval = (): void => { void qc.invalidateQueries({ queryKey: ["mkt-forms"] }); void qc.invalidateQueries({ queryKey: ["mkt-form-options"] }); };
  const onErr = (e: unknown): void => toast.error(e instanceof ApiError ? e.message : "No se pudo completar la acción.");

  const create = useMutation({
    mutationFn: () => api.post<{ form: MktForm }>("/mkt/forms", { name: name.trim(), kind: newKind, ...(folder ? { folder } : {}) }),
    onSuccess: (r) => { inval(); nav(`${r.form.id}/editar`); }, onError: onErr,
  });
  const dup = useMutation({
    mutationFn: (id: string) => api.post(`/mkt/forms/${id}/duplicate`),
    onSuccess: () => { toast.success("Formulario duplicado (borrador)."); inval(); }, onError: onErr,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/mkt/forms/${id}`),
    onSuccess: () => { toast.success("Formulario eliminado."); inval(); }, onError: onErr,
  });

  const rows = list.data?.rows ?? [];
  const go = (id: string, filter?: string): void => void nav(filter ? `${id}?tab=envios&filter=${filter}` : id);
  const num = (r: FormListRow, n: number, filter: string): JSX.Element => (
    <button type="button" className={`tnum ${n ? "link" : "text-ink-muted"}`} onClick={(e) => { e.stopPropagation(); go(r.id, filter); }}>{n}</button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h2 className="text-lg font-semibold text-ink">Formularios creados</h2>
        <div className="flex flex-wrap gap-2">
          <SearchInput value={q} onValueChange={setQ} placeholder="Buscar formulario…" className="w-56" />
          <Select className="w-auto" value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Tipo">
            <option value="">Todos los tipos</option>{(Object.keys(KIND_LABEL) as FormKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
          </Select>
          <Select className="w-auto" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Estado">
            <option value="">Todos los estados</option><option value="published">Publicado</option><option value="draft">Borrador</option>
          </Select>
          {admin && <Button variant="primary" onClick={() => setCreating(true)}><Plus size={16} strokeWidth={1.75} /> Nuevo formulario</Button>}
        </div>
      </div>

      <Modal open={creating} onClose={() => setCreating(false)} title="Nuevo formulario"
        footer={<><Button onClick={() => setCreating(false)}>Cancelar</Button><Button variant="primary" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()}>Crear y editar</Button></>}>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Nombre" required><Input value={name} onChange={(e) => setName(e.target.value)} maxLength={160} autoFocus placeholder="Ej.: Contacto web SACS" /></Field>
          <Field label="Tipo">
            <Select value={newKind} onChange={(e) => setNewKind(e.target.value as FormKind)}>{(Object.keys(KIND_LABEL) as FormKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}</Select>
          </Field>
        </div>
        <p className="text-xs text-ink-muted mt-3">Se crea como borrador con campos básicos (nombre, apellidos, correo, teléfono, empresa) y casilla de consentimiento.</p>
      </Modal>

      <div className="flex flex-col md:flex-row gap-4">
        <FolderNav folders={opts.data?.folders ?? []} value={folder} onChange={setFolder} />
        <Card className="flex-1 min-w-0 overflow-hidden">
          {list.isLoading ? <TableSkeleton rows={5} cols={8} />
            : list.isError ? <ErrorState message="No se pudieron cargar los formularios." onRetry={() => void list.refetch()} />
            : !rows.length ? <EmptyState title="Sin formularios" body={q || kind || status || folder ? "Ningún formulario coincide con los filtros." : "Crea un formulario para captar contactos desde la web de SACS."} />
            : (
              <div className="overflow-x-auto scrollbar-thin">
                <table className="w-full">
                  <thead><tr>
                    <th className="th">Formulario</th><th className="th">Tipo</th><th className="th">Creado por</th><th className="th">Estado</th><th className="th">Creación</th>
                    <th className="th text-right">Impresiones</th><th className="th text-right">Conversiones</th><th className="th text-right">Contactos creados</th><th className="th text-right">Empresas creadas</th><th className="th" />
                  </tr></thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.id} className="hover:bg-surface-hover cursor-pointer" onClick={() => go(r.id)}>
                        <td className="td"><div className="font-medium text-ink min-w-[180px]">{r.name}</div>{r.folder && <div className="text-xs text-ink-muted">{r.folder}</div>}</td>
                        <td className="td whitespace-nowrap">{KIND_LABEL[r.kind] ?? r.kind}</td>
                        <td className="td whitespace-nowrap">{r.creator ?? "—"}</td>
                        <td className="td"><Badge color={r.status === "published" ? "green" : "gray"}>{r.status === "published" ? "Publicado" : "Borrador"}</Badge></td>
                        <td className="td whitespace-nowrap">{formatDate(r.createdAt)}</td>
                        <td className="td text-right tnum">{r.impressions}</td>
                        <td className="td text-right">{num(r, r.conversions, "all")}</td>
                        <td className="td text-right">{num(r, r.contactsCreated, "contacts")}</td>
                        <td className="td text-right">{num(r, r.accountsCreated, "accounts")}</td>
                        <td className="td" onClick={(e) => e.stopPropagation()}>
                          <div className="flex justify-end gap-1">
                            {r.status === "published" && <button type="button" className="btn-ghost btn-sm" title="Ver detalle y código" onClick={() => go(r.id)}><ExternalLink size={14} /></button>}
                            {admin && <button type="button" className="btn-ghost btn-sm" title="Duplicar" onClick={() => dup.mutate(r.id)}><Copy size={14} /></button>}
                            {admin && <button type="button" className="btn-ghost btn-sm text-danger" title="Eliminar"
                              onClick={() => { if (window.confirm(`¿Eliminar el formulario «${r.name}» y todos sus envíos? Si está incrustado en una web dejará de mostrarse.`)) del.mutate(r.id); }}><Trash2 size={14} /></button>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </Card>
      </div>
    </div>
  );
}
